import React, { Component } from 'react';

import hoistNonReactStatic from 'hoist-non-react-statics';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
    ReactReduxContext,
    connect,
} from 'react-redux';

import { Redirect } from 'wanderu-component-react-router-link';
import appSettings from 'wanderu-config';
import {
    filterActions,
    filterSelectors,
    searchActions,
    searchSelectors,
} from 'wanderu-ducks-search';
import { utils as hocUtils } from 'wanderu-hocs';
import { selectLocale } from 'wanderu-i18n';
import {
    buildUrlParams,
    buildValidServerUrl,
    buildValidUrl,
    directionIsValid,
    directionIsValidOnServer,
    urlIsValid,
    urlIsValidOnServer,
} from 'wanderu-utils';

import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
} from '../../constants';

function directionIsValidHelper(searchParams) {
    const { isServer } = appSettings.env;
    const directionIsValidFn = isServer
        ? directionIsValidOnServer
        : directionIsValid;
    return directionIsValidFn(searchParams.direction, searchParams.returnDate);
}

function urlIsValidHelper(urlSearchParams, reduxSearchParams) {
    const urlParams = buildUrlParams(urlSearchParams, reduxSearchParams);
    const { isServer } = appSettings.env;
    const urlIsValidFn = isServer
        ? urlIsValidOnServer
        : urlIsValid;
    return urlIsValidFn(urlParams);
}

function buildRedirectUrl(searchParams, urlSearchParams) {
    const { isServer } = appSettings.env;
    const buildValidUrlFn = isServer
        ? buildValidServerUrl
        : buildValidUrl;

    return buildValidUrlFn({
        departDate: searchParams.departDate,
        departTripId: searchParams.departTripId,
        destinationPlaceId: searchParams.destinationPlaceId,
        destinationText: searchParams.destinationText || urlSearchParams.destinationText,
        direction: searchParams.direction,
        originPlaceId: searchParams.originPlaceId,
        originText: searchParams.originText || urlSearchParams.originText,
        originTimezone: undefined,
        passengers: searchParams.passengers,
        returnDate: searchParams.returnDate,
    });
}

function searchUrlManager(WrappedComponent) {
    class SearchUrlManager extends Component {
        static contextType = ReactReduxContext;

        static async serverFetchData(opts) {
            const {
                dispatch,
                getState,
                params,
                query,
            } = opts;

            const urlProps = {
                location: { search: query },
                match: { params },
            };

            const urlSearchParams = searchSelectors.selectUrlSearchParams(urlProps);
            const allUrlFilterParams = searchSelectors.selectUrlFilterParams(urlProps);
            const searchParams = {
                ...urlSearchParams,
                locale: params.locale,
            };

            await dispatch(searchActions.initSearchAndFilters(searchParams, allUrlFilterParams));

            const reduxSearchParams = searchSelectors.selectReduxSearchParams(getState());

            if (!urlIsValidHelper(urlSearchParams, reduxSearchParams)) {
                return undefined;
            }

            return WrappedComponent.serverFetchData(opts);
        }

        static displayName = `searchUrlManager(${hocUtils.getDisplayName(WrappedComponent)})`;

        constructor(props) {
            super(props);

            const {
                reduxSearchParams,
                urlSearchParams,
            } = this.props;

            this.state = {
                shouldRedirectToValidUrl: !urlIsValidHelper(urlSearchParams, reduxSearchParams),
            };
        }

        componentDidMount() {
            if (this.state.shouldRedirectToValidUrl) {
                return;
            }

            const {
                locale,
                urlSearchParams,
                allUrlFilterParams,
            } = this.props;

            const searchParams = {
                ...urlSearchParams,
                locale,
            };
            this.props.initSearchAndFilters(searchParams, allUrlFilterParams);
        }

        componentDidUpdate(prevProps) {
            const {
                locale,
                reduxSearchParams,
                urlSearchParams,
                reduxFilterParams,
                urlFilterParams,
                allUrlFilterParams,
                setQueryParams,
                setSearchParams,
            } = this.props;

            if (this.state.shouldRedirectToValidUrl) {
                this.setState(() => ({ shouldRedirectToValidUrl: false }));
            }

            if (urlSearchParams !== prevProps.urlSearchParams) {
                setSearchParams({
                    ...urlSearchParams,
                    locale,
                }, allUrlFilterParams);
            } else {
                if (reduxSearchParams !== prevProps.reduxSearchParams) {
                    this.syncUrlSearchParams();
                }

                if (urlFilterParams !== prevProps.urlFilterParams) {
                    setQueryParams(allUrlFilterParams);
                    this.syncReduxFilterParams();
                }

                if (!isEqual(reduxFilterParams, prevProps.reduxFilterParams)) {
                    this.syncUrlFilterParams();
                }
            }
        }

        componentWillUnmount() {
            this.props.removeSearchParams();
        }

        syncReduxFilterParams() {
            const {
                reduxFilterParams,
                urlFilterParams,
                urlSearchParams,
            } = this.props;
            if (!isEqual(urlFilterParams, reduxFilterParams)) {
                this.props.setFilterParams(urlFilterParams, urlSearchParams);
            }
        }

        syncUrlSearchParams() {
            const {
                reduxSearchParams,
                urlSearchParams,
            } = this.props;

            if (!isEqual(urlSearchParams, reduxSearchParams)) {
                this.setState(() => ({ shouldRedirectToValidUrl: true }));
            }
        }

        syncUrlFilterParams() {
            const {
                reduxFilterParams,
                urlFilterParams,
            } = this.props;

            if (!isEqual(reduxFilterParams, urlFilterParams)) {
                this.setState(() => ({ shouldRedirectToValidUrl: true }));
            }
        }

        render() {
            const { shouldRedirectToValidUrl } = this.state;
            const {
                urlSearchParams,
                reduxSearchParams,
                reduxDepartFilterParams,
                reduxReturnFilterParams,
                shouldRedirectToOneWay,
                location,
            } = this.props;

            const reduxAllFilterParams = {
                ...reduxDepartFilterParams,
                ...reduxReturnFilterParams,
            };

            const shouldRedirect = shouldRedirectToValidUrl || shouldRedirectToOneWay;
            const queries = directionIsValidHelper(reduxSearchParams)
                ? { ...reduxAllFilterParams }
                : undefined;

            if (shouldRedirectToOneWay) {
                queries.dtid = undefined;
            }

            return (
                <>
                    {shouldRedirect && (
                        <Redirect
                            href={buildRedirectUrl(
                                reduxSearchParams,
                                urlSearchParams,
                            )}
                            push={urlIsValidHelper(reduxSearchParams, urlSearchParams)}
                            queries={queries}
                        />
                    )}
                    <WrappedComponent
                        location={this.props.location}
                        match={this.props.match}
                        route={this.props.route}
                    />
                </>
            );
        }
    }

    const mapDispatchToProps = {
        initSearchAndFilters: searchActions.initSearchAndFilters,
        removeSearchParams: searchActions.removeSearchParams,
        setFilterParams: filterActions.setFilterParams,
        setQueryParams: searchActions.setQueryParams,
        setSearchParams: searchActions.setSearchParams,
        syncFilterParams: filterActions.syncFilterParams,
    };

    function mapStateToProps(state, ownProps) {
        const urlSearchParams = searchSelectors.selectUrlSearchParams(ownProps);
        const {
            direction,
        } = urlSearchParams;

        const reduxDepartFilterParams = filterSelectors.selectFilterQueryParams(
            state,
            {
                ...urlSearchParams,
                direction: DEPART_DIRECTION,
            },
        );

        const reduxReturnFilterParams = filterSelectors.selectFilterQueryParams(
            state,
            {
                ...urlSearchParams,
                direction: RETURN_DIRECTION,
            },
        );

        const shouldRedirectToOneWay = direction === DEPART_DIRECTION
            && !!urlSearchParams.departTripId;

        return {
            allUrlFilterParams: searchSelectors.selectUrlFilterParams(ownProps),
            locale: selectLocale(state),
            reduxDepartFilterParams,
            reduxFilterParams: direction === RETURN_DIRECTION
                ? reduxReturnFilterParams
                : reduxDepartFilterParams,
            reduxReturnFilterParams,
            reduxSearchParams: searchSelectors.selectReduxSearchParams(state),
            shouldRedirectToOneWay,
            urlFilterParams: searchSelectors.selectUrlFilterParams(ownProps, direction),
            urlSearchParams,
        };
    }

    const ConnectedSearchUrlManager = connect(
        mapStateToProps,
        mapDispatchToProps,
        // eslint-disable-next-line react-redux/prefer-separate-component-file
    )(SearchUrlManager);

    ConnectedSearchUrlManager.contextType = ReactReduxContext;

    return hoistNonReactStatic(ConnectedSearchUrlManager, WrappedComponent, {
        serverFetchData: true,
    });
}

export default searchUrlManager;
